import { useEffect, useMemo, useRef, useState } from "react";
import {
  initSimulation,
  parentByTarget,
  positionsFromSimulation,
  simulationEnergy,
  simulationTick,
} from "./forceSimulation";

/**
 * Animate nodes toward a force-balanced layout anchored on radial targets.
 *
 * @param {!Object<string, {x: number, y: number}>} anchorPositions
 * @param {!Array<{source: string, target: string}>} edges
 * @param {string|null} rootKey
 * @param {!Array<string>} nodeIds
 */
export function useForceDirectedPositions(anchorPositions, edges, rootKey, nodeIds) {
  const [positions, setPositions] = useState({});
  const [isSimulating, setIsSimulating] = useState(false);
  const positionsRef = useRef({});
  const rafRef = useRef(0);

  const layoutKey = useMemo(() => {
    const anchors = Object.keys(anchorPositions)
      .sort()
      .map(id => {
        const p = anchorPositions[id];
        return `${id}:${Math.round(p.x)}:${Math.round(p.y)}`;
      })
      .join("|");
    const edgeKey = edges
      .map(edge => `${edge.source}>${edge.target}`)
      .sort()
      .join("|");
    return `${rootKey || ""}::${nodeIds.slice().sort().join(",")}::${anchors}::${edgeKey}`;
  }, [anchorPositions, edges, nodeIds, rootKey]);

  useEffect(() => {
    if (!rootKey || !nodeIds.length) {
      setPositions({});
      setIsSimulating(false);
      positionsRef.current = {};
      return undefined;
    }

    const parents = parentByTarget(edges);
    const nodes = initSimulation(
      nodeIds,
      anchorPositions,
      rootKey,
      positionsRef.current,
      parents,
    );
    setIsSimulating(true);
    let frame = 0;

    const tick = () => {
      simulationTick(nodes, edges);
      const next = positionsFromSimulation(nodes);
      positionsRef.current = next;
      setPositions(next);
      frame += 1;
      const energy = simulationEnergy(nodes);
      if (frame >= 200 || energy < 0.06) {
        setIsSimulating(false);
        return;
      }
      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(rafRef.current);
    };
  }, [layoutKey, anchorPositions, edges, nodeIds, rootKey]);

  return { positions, isSimulating };
}
